/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './user-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {ApiConfig} from '../../../components/api/api';
import {LocaleHelper} from '../../../components/util/locale-helper';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import {LOCALES} from '../../common/constants/locales';
import {TwoFactorAuthRecoveryModalComponent} from './two-factor-auth-recovery-modal.component';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {RoleResource} from '../../../components/api/resources/resource/role.resource';
import {TeamGroupResource} from '../../../components/api/resources/resource/team-group.resource';
import {NetworkResource} from '../../../components/api/resources/resource/network.resource';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {AuthenticatedUserResource} from '../../../components/api/resources/resource/authenticated-user';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {LoginStatsService} from '../../../components/api/resources/resource/login-stats.service';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {BrowserLocationService, WINDOW} from '../../angular-platform-providers';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-user-edit-page',
  template
})
export class UserEditPageComponent extends AdminResourceEditPageBase {
  readonly locales = LOCALES;

  blockFields = false;
  showChangePassword = false;
  showsSurname = false;
  updatePassword = false;
  showPasswordChars = false;
  showSlim = false;
  showSlimBasis = false;
  showLogin = false;
  isCurrentUser = false;
  isAxPublisherAdmin = false;
  isAxAdvertiserAdmin = false;
  isSuiAdmin = false;
  isAxAdmin = false;
  inNetwork = false;

  adjustedLastLogin: Date | null = null;
  adjustedLastPasswordChange: Date | null = null;
  lastLogins: any[] = [];

  currencies: string[] = [];
  defaultCurrency: string;
  applications: string[] = [];
  networkGroup: any = {};
  networkRoles: any = {};
  roles: any = {};
  teams: any[] | null = null;

  private readonly networkId: string;
  private readonly baseApplications: string[] = [];
  private rolesByGroup: any[] = [];
  private teamGroups: any[] = [];
  private showSlimDecided = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(WINDOW) private readonly window: any,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(LoginStatsService) private readonly LoginStats: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(TeamGroupResource) private readonly TeamGroup: any,
    @Inject(RoleResource) private readonly Role: any,
    @Inject(NetworkResource) private readonly Network: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(AuthenticatedUserResource) private readonly authenticatedUserResource: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly angularModalService: AngularModalService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.networkId = LocalNetworkProfile.getNetworkId();

    if (LocalNetworkProfile.isDataPlatform()) {
      this.baseApplications.push('DATA');
    }
    if (LocalNetworkProfile.isAdvertisingPlatform()) {
      this.baseApplications.push('ADVERTISING');
    }
    if (LocalNetworkProfile.get('politicalApp')) {
      this.baseApplications.push('POLITICAL');
    }

    this.applications = _.filter(this.LocalUserPermissions.getApps(), (a: string) => a !== 'SELF_SERVICE');
    const currentApp = localUserProfile.getCurrentApp();
    this.applications = _.orderBy(this.applications, (a: string) => a === currentApp ? 0 : 1);

    this.currencies = LocalNetworkProfile.get('availableCurrencies') || [];
    this.defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.networkGroup = {tagId: this.networkId};
    this.showsSurname = LocalNetworkProfile.showsSurname();
    this.initializeShowSlimView();

    const username = localUserProfile.get('username');
    const isLocalhost = this.location.hostname === 'localhost';
    const env = ApiConfig.obtain().getEnv();
    const isSpecialUser = (isLocalhost && username.startsWith('admin@'))
      || username === 'adnuntius@adnuntius.com'
      || username === 'broker1@adnuntius.com';
    this.showChangePassword = isSpecialUser && env !== 'production';

    this.isAxPublisherAdmin = this.LocalUserPermissions.hasNetworkPermission('AX_PUBLISHER_ADMIN')
      || this.LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN');
    this.isAxAdvertiserAdmin = this.LocalUserPermissions.hasNetworkPermission('AX_ADVERTISER_ADMIN')
      || this.LocalUserPermissions.hasAny('AX_ADVERTISER_TEAM_ADMIN');
    this.isSuiAdmin = this.LocalUserPermissions.hasNetworkPermission('SELF_SERVICE_USER_ADMIN');
    this.isAxAdmin = this.isAxPublisherAdmin || this.isAxAdvertiserAdmin;
  }

  cancelPasswordUpdate(): void {
    this.updatePassword = false;
    this.editingModel.password = '';
    this.editingModel.password2 = '';
    this.markExternalFormDirty();
  }

  initiate2faRecovery(): void {
    this.angularModalService.show(TwoFactorAuthRecoveryModalComponent, {
      class: 'modal-lg',
      initialState: {
        userId: this.editingModel.id,
        networkId: this.networkId
      }
    });
  }

  protected afterInitEditingModel(): void {
    this.blockFields = this.editingModel.readOnly === true
      || (this.editingModel.username === 'adnuntius@adnuntius.com'
        && this.localUserProfile.get('username') !== 'adnuntius@adnuntius.com');
    this.isCurrentUser = this.editingModel.id === this.localUserProfile.get('userId');
    this.adjustedLastLogin = this.editingModel.lastLogin ? this.adnTimeShifter.timeShift(this.editingModel.lastLogin) : null;
    this.adjustedLastPasswordChange = this.editingModel.lastPasswordChange
      ? this.adnTimeShifter.timeShift(this.editingModel.lastPasswordChange)
      : null;

    if (this.isNew) {
      this.editingModel.objectState = 'ACTIVE';
    }
    this.updatePassword = false;
    this.editingModel.password = '';

    this.rolesByGroup = [];
    if (this.editingModel.userRoles) {
      const rolesByNetwork = _.find(this.editingModel.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
      if (rolesByNetwork?.roles) {
        this.rolesByGroup = _.cloneDeep(rolesByNetwork.roles);
      }
    }

    this.editingModel.locale = this.editingModel.locale || LocaleHelper.getNavigatorLanguage(this.window);
    if (LOCALES.indexOf(this.editingModel.locale) < 0) {
      const split = this.editingModel.locale.split('_');
      if (LOCALES.indexOf(split[0]) > -1) {
        this.editingModel.locale = split[0];
      }
      if (LOCALES.indexOf(this.editingModel.locale) < 0) {
        this.editingModel.locale = 'en_GB';
      }
    }

    this.inNetwork = this.LocalUserPermissions.userPartOf(this.editingModel, this.networkId);
    this.ensureNetworkGroupStructure();
    this.loadRolesData();

    if (!this.isNew && this.editingModel.id) {
      this.LoginStats.getForUser(this.editingModel.id).then((data: any[]) => {
        this.ngZone.run(() => {
          this.lastLogins = _.map(data, (login: any) => _.assign({}, login, {
            adjustedDateTime: this.adnTimeShifter.timeShift(login.dateTime)
          }));
        });
      });
    }
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this.updatePassword = false;
    this.showPasswordChars = false;
    this.editingModel.password = '';

    if (this.editingModel.id === this.localUserProfile.get('userId')) {
      this.authenticatedUserResource.get().then((authUser: any) => {
        this.LocalUserPermissions.create(authUser);
      });
    }

    if (this.localUserProfile.get('userId') === this.editingModel.id) {
      this.localUserProfile.set('locale', this.editingModel.locale);
      this.localUserProfile.set('displayName', this.editingModel.displayName);
      this.localUserProfile.set('name', this.editingModel.name);
      this.localUserProfile.set('canMasquerade', this.editingModel.canMasquerade);
      this.localUserProfile.set('preferredCurrency', this.editingModel.preferredCurrency);
    }
    this.inNetwork = this.LocalUserPermissions.userPartOf(this.editingModel, this.networkId);

    if (saveObj) {
      this.rolesByGroup = [];
      if (this.editingModel.userRoles) {
        const rolesByNetwork = _.find(this.editingModel.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
        if (rolesByNetwork?.roles) {
          this.rolesByGroup = _.cloneDeep(rolesByNetwork.roles);
        }
      }
      this.assignRoleRefsToGroups(this.teams || [], this.teamGroups);
      this.applyRolesToEditingModel();
    }
  }

  protected onBeforeSubmit(): void {
    this.editingModel.twoFactorAuthType = null;
    this.editingModel.login2faRequired = null;

    const baseNetworkRoles: any[] = [];
    _.forEach(this.baseApplications, (app: string) => {
      if (_.get(this.editingModel.networkGroup, [app, 'role'])) {
        baseNetworkRoles.push({
          network: {id: this.networkId},
          role: {id: this.editingModel.networkGroup[app].role.id}
        });
      }
    });
    this.editingModel.userRoles = {rolesByNetwork: [{network: {id: this.networkId}, roles: baseNetworkRoles}]};

    const networkRoles = _.find(this.editingModel.userRoles.rolesByNetwork, ['network.id', this.networkId]);
    if (networkRoles?.roles) {
      _.forEach(this.editingModel.teams, (team: any) => {
        _.forEach(this.applications, (app: string) => {
          const appTeam = team[app];
          if (appTeam?.role?.id) {
            networkRoles.roles.push({
              team: {id: team.id},
              role: {id: appTeam.role.id}
            });
          }
        });
      });
      _.forEach(this.editingModel.teamGroups, (teamGroup: any) => {
        _.forEach(this.applications, (app: string) => {
          const appTeam = teamGroup[app];
          const hasManageSystemPerm = _.get(this.editingModel, ['networkGroup', app, 'role', 'permissions'], [])
            .indexOf('MANAGE_SYSTEM') > -1;
          if (appTeam?.role?.id) {
            networkRoles.roles.push({
              teamGroup: {id: teamGroup.id},
              role: (appTeam.role?.id && hasManageSystemPerm) ? {id: appTeam.role.id} : {id: null}
            });
          }
        });
      });
    }
  }

  /**
   * Collapsed roles view when the network has more than 10 teams (legacy user-controller behaviour).
   * Prefer cached teamsCount; if that is not loaded yet, decide after teams are fetched.
   */
  private initializeShowSlimView(loadedTeamCount?: number): void {
    if (this.showSlimDecided) {
      return;
    }

    const cachedCount = Number(LocalNetworkInfo.obtain().getInfo('teamsCount'));
    let count = cachedCount;
    if (!Number.isFinite(count) || count <= 0) {
      if (loadedTeamCount === undefined) {
        return;
      }
      count = loadedTeamCount;
    }

    this.showSlim = count > 10;
    this.showSlimBasis = this.showSlim;
    this.showSlimDecided = true;
  }

  private ensureNetworkGroupStructure(): void {
    this.editingModel.networkGroup = this.editingModel.networkGroup || _.cloneDeep(this.networkGroup);
    _.forEach(this.applications, (app: string) => {
      if (!this.editingModel.networkGroup[app]) {
        this.editingModel.networkGroup[app] = {};
      }
    });
  }

  private loadRolesData(): void {
    let queryType: any = {pageSize: 2000, minimal: true};
    if (this.isAxPublisherAdmin && this.isAxAdvertiserAdmin) {
      queryType = {where: 'type in AX_PUBLISHER,AX_ADVERTISER'};
    } else if (this.isAxPublisherAdmin) {
      queryType = {where: 'type = AX_PUBLISHER'};
    } else if (this.isAxAdvertiserAdmin) {
      queryType = {where: 'type = AX_ADVERTISER'};
    } else if (this.isSuiAdmin) {
      queryType = {where: 'type in SUI'};
    }

    const queries: Promise<any>[] = [
      this.Team.query(queryType).$promise.then((page: any) => page.results),
      this.Network.get({id: this.networkId}).$promise.then((network: any) => network),
      this.Role.query().$promise.then((page: any) => page.results)
    ];

    if (this.baseApplications.indexOf('ADVERTISING') > -1) {
      queries.push(this.TeamGroup.query(queryType).$promise.then((page: any) => page.results));
    }

    Promise.all(queries).then((results: any[]) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.teams = results[0];
        this.initializeShowSlimView(this.teams.length);
        const network = results[1];
        const allRoles = results[2];
        this.teamGroups = results[3] || [];

        this.networkGroup.name = network.name;
        this.buildRoleCatalog(allRoles);
        this.assignRoleRefsToGroups(this.teams, this.teamGroups);
        this.applyRolesToEditingModel();
      });
    });
  }

  private buildRoleCatalog(allRoles: any[]): void {
    _.forEach(this.applications, (app: string) => {
      this.networkRoles[app] = _.filter(allRoles, (r: any) => {
        if (!this.isCurrentUser && this.isAxAdmin) {
          return r.scope === 'NETWORK' && r.application === app && r.permissions.length === 0;
        }
        return r.scope === 'NETWORK' && r.application === app;
      });

      this.roles[app] = this.filterTeamRoles(allRoles, app);
    });
  }

  private filterTeamRoles(allRoles: any[], app: string): any {
    if (app === 'ADVERTISING') {
      const teamRoles: any[] = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app
        && r.permissions.indexOf('SELF_SERVICE_OWN_ADVERTISING') === -1
        && r.permissions.indexOf('AX_ADVERTISER') === -1
        && r.permissions.indexOf('AX_PUBLISHER') === -1
        && r.permissions.indexOf('CREATIVE_AGENCY') === -1);
      (teamRoles as any).AX_ADVERTISER = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app
        && ((!this.isAxAdvertiserAdmin && r.permissions.indexOf('SELF_SERVICE_OWN_ADVERTISING') === -1
          && r.permissions.indexOf('CREATIVE_AGENCY') === -1)
          || r.permissions.indexOf('AX_ADVERTISER') >= 0
          || r.permissions.indexOf('AX_PUBLISHER') >= 0));
      (teamRoles as any).AX_PUBLISHER = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app
        && ((!this.isAxPublisherAdmin && r.permissions.indexOf('AX_ADVERTISER') === -1
          && r.permissions.indexOf('SELF_SERVICE_OWN_ADVERTISING') === -1
          && r.permissions.indexOf('CREATIVE_AGENCY') === -1)
          || r.permissions.indexOf('AX_PUBLISHER') >= 0));
      (teamRoles as any).SUI = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app
        && ((!this.isSuiAdmin && r.permissions.indexOf('AX_ADVERTISER') === -1 && r.permissions.indexOf('AX_PUBLISHER') === -1)
          || r.permissions.indexOf('SELF_SERVICE_OWN_ADVERTISING') >= 0));
      (teamRoles as any).CREATIVE_AGENCY = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app
        && ((!this.isAxAdvertiserAdmin && r.permissions.indexOf('AX_ADVERTISER') === -1 && r.permissions.indexOf('AX_PUBLISHER') === -1)
          || r.permissions.indexOf('CREATIVE_AGENCY') >= 0));
      return teamRoles;
    }

    if (app === 'POLITICAL') {
      const politicalTeamRoles = _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app);
      (politicalTeamRoles as any).SPONSOR = _.filter(politicalTeamRoles, (r: any) => r.permissions.indexOf('CONTROLLER') === -1);
      (politicalTeamRoles as any).CONTROLLER = _.filter(politicalTeamRoles, (r: any) => r.permissions.indexOf('SPONSOR') === -1);
      return politicalTeamRoles;
    }

    return _.filter(allRoles, (r: any) => r.scope === 'TEAM' && r.application === app);
  }

  private assignRoleRefsToGroups(teams: any[], teamGroups: any[]): void {
    _.forEach(teams, (team: any) => {
      _.forEach(this.applications, (app: string) => {
        if (!team[app]) {
          team[app] = {};
        }
        const roleTeam = _.find(this.rolesByGroup, (rg: any) => rg.team?.id === team.id
          && _.get(rg.role, 'application') === app);
        if (roleTeam) {
          _.set(team, [app, 'role'], roleTeam.role);
        }
      });
    });

    _.forEach(teamGroups, (teamGroup: any) => {
      _.forEach(this.applications, (app: string) => {
        if (!teamGroup[app]) {
          teamGroup[app] = {};
        }
        const roleTeam = _.find(this.rolesByGroup, (rg: any) => rg.teamGroup?.id === teamGroup.id
          && _.get(rg.role, 'application') === app);
        if (roleTeam) {
          _.set(teamGroup, [app, 'role'], roleTeam.role);
        }
      });
    });

    _.forEach(this.applications, (app: string) => {
      const rg = _.find(this.rolesByGroup, (entry: any) => !entry.team && !entry.teamGroup
        && _.get(entry.role, 'application') === app);
      if (rg) {
        _.set(this.networkGroup, [app, 'role'], rg.role);
      }
    });
  }

  private findTeamRoleInCatalog(app: string, teamType: string, roleId: string): any {
    const appRoles = this.roles[app];
    if (!appRoles || !roleId) {
      return null;
    }
    let candidates: any[];
    if (teamType !== 'STANDARD' && appRoles[teamType]) {
      candidates = appRoles[teamType];
    } else if (Array.isArray(appRoles)) {
      candidates = appRoles;
    } else {
      candidates = [];
    }
    return _.find(candidates, (role: any) => role.id === roleId) || null;
  }

  private reconcileGroupRoleRefs(groups: any[]): void {
    _.forEach(groups, (group: any) => {
      _.forEach(this.applications, (app: string) => {
        const currRole = _.get(group, [app, 'role']);
        if (currRole?.id) {
          const foundRole = this.findTeamRoleInCatalog(app, group.type, currRole.id);
          if (foundRole) {
            group[app].role = foundRole;
          }
        }
      });
    });
  }

  private applyRolesToEditingModel(): void {
    if (!this.teams) {
      return;
    }

    this.editingModel.networkGroup = this.editingModel.networkGroup || _.cloneDeep(this.networkGroup);
    this.ensureNetworkGroupStructure();

    _.forEach(this.applications, (app: string) => {
      const roleFromNetworkGroup = _.get(this.networkGroup, [app, 'role']);
      if (roleFromNetworkGroup) {
        const foundRole = _.find(this.networkRoles[app], (role: any) => role.id === roleFromNetworkGroup.id);
        this.editingModel.networkGroup[app].role = foundRole || roleFromNetworkGroup;
        return;
      }
      const currRole = _.get(this.editingModel, ['networkGroup', app, 'role']);
      if (currRole) {
        const foundRole = _.find(this.networkRoles[app], (role: any) => role.id === currRole.id);
        if (foundRole) {
          this.editingModel.networkGroup[app].role = foundRole;
        }
      }
    });

    this.editingModel.teams = _.cloneDeep(this.teams);
    this.editingModel.teamGroups = _.cloneDeep(this.teamGroups);
    this.reconcileGroupRoleRefs(this.editingModel.teams);
    this.reconcileGroupRoleRefs(this.editingModel.teamGroups);
  }
}
